import React, { useState } from 'react';
import BulkQuantityInput from '@ajat/design-system/BulkQuantityInput';
import { ComponentPage, DemoCard } from '../shared';

const SIZES = ['S', 'M', 'L', 'XL', 'XXL'];
const MIN_TOTAL = 10;

export const meta = {
  component: BulkQuantityInput,
  title: 'BulkQuantityInput',
  group: 'Forms',
  importPath: '@ajat/design-system/BulkQuantityInput',
  figmaNode: '3796:14022',
  description:
    'A stepper row with a size square on the left — `[ S ][ 3 ][− +]`. One row per size on the bulk-order pages, where the customer buys by size rather than configuring a single garment. Arrow keys change the value when the row has focus.',
  props: [
    ['sizeLabel', 'string', '—', 'Size shown in the square, e.g. "S" or "XL" (required)'],
    ['value', 'number', '—', 'Controlled value'],
    ['defaultValue', 'number', '0', 'Initial value (uncontrolled)'],
    ['min', 'number', '0', 'Minimum — the minus button disables here'],
    ['max', 'number', '999', 'Maximum — the plus button disables here'],
    ['step', 'number', '1', 'Increment per click'],
    ['onChange', 'func', '—', 'Fires with the new numeric value'],
    ['error', 'string', "''", 'Error message, shown as a tooltip below'],
    ['disabled', 'bool', 'false', 'Disables the whole row'],
    ['className', 'string', "''", 'Additional CSS class'],
    ['id', 'string', '—', 'Id of the hidden input'],
    ['name', 'string', '—', 'Name of the hidden input'],
  ],
  usage: [
    {
      title: 'A bulk-order size list',
      code: `import BulkQuantityInput from '@ajat/design-system/BulkQuantityInput';

const [qty, setQty] = useState({});
const total = Object.values(qty).reduce((sum, n) => sum + n, 0);

{SIZES.map((size) => (
  <BulkQuantityInput
    key={size}
    sizeLabel={size}
    value={qty[size] ?? 0}
    onChange={(value) => setQty((prev) => ({ ...prev, [size]: value }))}
  />
))}

<StickyFooter
  buttonDisabled={total < MIN_TOTAL}
  buttonLabel={total < MIN_TOTAL ? \`Min. \${MIN_TOTAL} stk.\` : 'Læg i kurv'}
/>`,
    },
  ],
};

export default function BulkQuantityInputPage() {
  const [qty, setQty] = useState({ S: 0, M: 4, L: 2, XL: 0, XXL: 0 });
  const total = Object.values(qty).reduce((sum, n) => sum + n, 0);

  return (
    <ComponentPage meta={meta}>
      <DemoCard title="States">
        <div className="flex flex-col gap-[var(--space-ui-medium-s)] max-w-md">
          <BulkQuantityInput sizeLabel="S" defaultValue={0} />
          <BulkQuantityInput sizeLabel="M" defaultValue={4} />
          <BulkQuantityInput sizeLabel="XL" defaultValue={12} disabled />
        </div>
      </DemoCard>

      <DemoCard title="Error" hint="the tooltip is absolute — the row height does not change">
        <div className="max-w-md pb-[32px]">
          <BulkQuantityInput sizeLabel="L" defaultValue={0} error="Vælg mindst 1 stk." />
        </div>
      </DemoCard>

      <DemoCard title="A full size list" hint={`total: ${total} — minimum is ${MIN_TOTAL}`}>
        <div className="flex flex-col gap-[var(--space-ui-medium-s)] max-w-md">
          {SIZES.map((size) => (
            <BulkQuantityInput
              key={size}
              sizeLabel={size}
              value={qty[size]}
              onChange={(value) => setQty((prev) => ({ ...prev, [size]: value }))}
            />
          ))}
        </div>
        <p
          className="typo-body-sm"
          style={{ paddingTop: 'var(--space-ui-medium-s)', color: total < MIN_TOTAL ? 'var(--color-ui-error)' : 'var(--color-ui-success)' }}
        >
          {total < MIN_TOTAL ? `Tilføj ${MIN_TOTAL - total} mere for at nå minimum` : `${total} stk. — klar til kurven`}
        </p>
      </DemoCard>
    </ComponentPage>
  );
}
